import { Checkbox, Icon, Loading } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as TableS from '@hero/components/molecules/Table/style';
import { QUERY_KEYS } from '@hero/constants';
import { contactServices } from '@hero/services';
import { useGetTagList, useUpdateContactsTags } from '@hero/services/contact-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { cleanObjectEmptyFields, formatNumber } from '@hero/utils';

import { AddTagsToSelectedContacts } from './AddTagsToSelectedContacts';
import * as S from './style';

function CustomSelectionHead({ table }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const { mutateAsync: updateContactsTags } = useUpdateContactsTags();

  const { data: filterOptions } = useGetTagList();

  const [toggleSelectAll, setToggleSelectAll] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [tags, setTags] = useState([]);

  const showingSelectedItems = useMemo(() => {
    return table.getState()?.showingSelectedItems;
  }, [table.getState()?.showingSelectedItems]);

  const globalFilters = useMemo(() => {
    return table.getState()?.globalFilters;
  }, [table.getState()?.globalFilters]);

  const rowSelection = useMemo(() => {
    return table.getState()?.rowSelection;
  }, [table.getState()?.rowSelection]);

  const totalRowCount = useMemo(() => {
    return table.getState()?.pagination?.total;
  }, [table.getState()?.pagination?.total]);

  const selectedRowCount = useMemo(() => {
    return Object.keys(rowSelection || {}).length;
  }, [rowSelection]);

  const onToggleSelectAll = async () => {
    if (toggleSelectAll) {
      table.resetRowSelection();
    } else {
      const searchTerm = globalFilters.searchTerm;
      const tags = globalFilters.filters?.map(filter => filter.value);
      const ids = showingSelectedItems ? Object.keys(rowSelection) : undefined;

      setIsLoading(true);
      const data = await queryClient.fetchQuery(['selectAll', searchTerm, tags, ids], () => {
        return contactServices.selectAll(
          cleanObjectEmptyFields({
            filters: {
              searchTerm: globalFilters.searchTerm,
              tags: globalFilters.filters?.map(filter => filter.value),
              ids: showingSelectedItems ? Object.keys(rowSelection) : undefined
            }
          })
        );
      });

      if (data?.docs?.length)
        table.setRowSelection(
          data.docs.reduce((result, doc) => ({ ...result, [doc.id]: true }), {})
        );

      setIsLoading(false);
    }

    setToggleSelectAll(!toggleSelectAll);
  };

  React.useEffect(() => {
    if (selectedRowCount === 0) {
      setToggleSelectAll(false);
    }
    if (selectedRowCount === totalRowCount) {
      setToggleSelectAll(true);
    }
  }, [selectedRowCount, totalRowCount]);

  return (
    <TableS.HeadSelected>
      <TableS.HeadCheckbox>
        <Checkbox
          {...{
            key: table.getState().pagination.pageIndex,
            name: 'selectAll',
            indeterminate: table.getIsSomeRowsSelected(),
            checked: table.getIsAllRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler()
          }}
        />
      </TableS.HeadCheckbox>
      <TableS.HeadSelectedItems>
        <TableS.HeadSelectedItems>
          <Icon name="contacts" px={32} space={7} />
          <S.SelectedContactText>
            {t(toggleSelectAll ? 'nContactAreSelected' : 'selectedRowCount', {
              selectedRowCount: selectedRowCount,
              selectedTypo: selectedRowCount,
              totalContactCount: totalRowCount
            })}
          </S.SelectedContactText>
        </TableS.HeadSelectedItems>
        <S.SelectAllArea>
          {!(!toggleSelectAll && totalRowCount - selectedRowCount <= 0) &&
            table.getPageCount() > 1 && (
              <S.SelectAllContactButton color="neutral" variant="link" onClick={onToggleSelectAll}>
                {t(toggleSelectAll ? 'clearSelection' : 'selectRemainingNType', {
                  totalContactCount: formatNumber(totalRowCount - selectedRowCount),
                  type: t('contacts').toLocaleLowerCase()
                })}
              </S.SelectAllContactButton>
            )}

          {isLoading && <Loading size="sm" type="ring" />}
        </S.SelectAllArea>
      </TableS.HeadSelectedItems>
      <S.CustomSelectionHeadCenter>
        <AddTagsToSelectedContacts
          tags={tags}
          setTags={setTags}
          options={filterOptions}
          inputPlaceholderOnMenu={t('createOrFindATag')}
          onApply={async () => {
            const state = table.getState();
            const selectedContactIds = Object.keys(state.rowSelection);

            if (tags.length > 0 && selectedContactIds.length > 0) {
              await updateContactsTags({
                contactIds: selectedContactIds,
                tags: tags.map(tag => tag.value)
              });

              showSuccess({
                message: t('tagModalNotification', { length: selectedContactIds.length }),
                type: 'toast'
              });

              queryClient.setQueriesData([QUERY_KEYS.useGetContactsKey], data => {
                if (data.docs) {
                  return {
                    ...data,
                    docs: data.docs?.map?.(doc => {
                      if (selectedContactIds.includes(doc.id)) {
                        return {
                          ...doc,
                          tags: [
                            ...doc.tags,
                            ...filterOptions.docs.filter(
                              option =>
                                tags.filter(tag => tag.value === option.id).length > 0 &&
                                doc.tags.filter(t => t.id === option.id).length === 0
                            )
                          ]
                        };
                      }

                      return doc;
                    })
                  };
                }
                return data;
              });
            }

            setTags([]);
          }}
        />
      </S.CustomSelectionHeadCenter>
    </TableS.HeadSelected>
  );
}

export { CustomSelectionHead };
